import React from "react";
import {
	Button,
	ButtonProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	PartialKeys,
	Row,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { CalendarEvent, CalendarEventDetailQuery, useCalendarEventDetailQuery } from "../../../../Utilities";
import { CalendarEventDeleteButton } from "../../../DeleteButtons";
import { CalendarEventForm } from "../../../Forms";
import { CalendarEventModelIcon } from "../../_General/ModelIcons";
import { CalendarEventDeletePermissionRequired } from "../PermissionRequired";
import styles from "./styles.module.css";

export type ControlledCalendarEventUpdateModalControllerProps = Pick<
	ButtonProps,
	"variant" | "size" | "title" | "className" | "style" | "children"
> &
	Omit<ControlledCalendarEventUpdateModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ControlledCalendarEventUpdateModalController({
	variant,
	size,
	title,
	className,
	style,
	children,
	...props
}: ControlledCalendarEventUpdateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant={variant} size={size} title={title} onClick={openModal} className={className} style={style}>
				{children}
			</Button>

			<Modal.If condition={isModalOpen}>
				<ControlledCalendarEventUpdateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type CalendarEventUpdateModalControllerProps = Pick<
	ButtonProps,
	"variant" | "size" | "title" | "className" | "style" | "children"
> &
	Omit<CalendarEventUpdateModalProps, "ifRef" | "onClose" | "className" | "style">;

export function CalendarEventUpdateModalController({
	variant,
	size,
	title,
	className,
	style,
	children,
	...props
}: CalendarEventUpdateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant={variant} size={size} title={title} onClick={openModal} className={className} style={style}>
				{children}
			</Button>

			<Modal.If condition={isModalOpen}>
				<CalendarEventUpdateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

export type ControlledCalendarEventUpdateModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style"> &
	PartialKeys<
		Omit<
			CalendarEventForm.ControlledDetailProps,
			"calendarEvent" | "className" | "style" | "columnClassName" | "columnStyle"
		>,
		"onSuccess"
	> & {
		calendarEventID: CalendarEvent["id"];
	};

export function ControlledCalendarEventUpdateModal({
	calendarEventID,
	...props
}: ControlledCalendarEventUpdateModalProps) {
	const { loading, data } = useCalendarEventDetailQuery({ variables: { id: calendarEventID } });

	return <CalendarEventUpdateModal isLoading={loading} calendarEvent={data?.calendarEvent} {...props} />;
}

const formStateArgs = { editMode: EditMode.WriteOnly };

export type CalendarEventUpdateModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style"> &
	PartialKeys<
		Omit<
			CalendarEventForm.ControlledDetailProps,
			"calendarEvent" | "className" | "style" | "columnClassName" | "columnStyle"
		>,
		"onSuccess"
	> & {
		isLoading?: boolean;
		calendarEvent: CalendarEventDetailQuery["calendarEvent"] | null | undefined;
	};

export function CalendarEventUpdateModal({
	calendarEvent,
	ifRef,
	onClose,
	onSuccess,
	className,
	style,
	...props
}: CalendarEventUpdateModalProps) {
	const fullOnSuccess = React.useCallback<CalendarEventForm.ControlledDetailProps["onSuccess"]>(
		(formState, data) => {
			onSuccess?.(formState, data);
			onClose?.();
		},
		[onClose, onSuccess],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["update-modal"], className)} style={style}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<CalendarEventModelIcon size={22} />
					<Heading.H2 noMargin>Edit Event</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<Conditional>
					<If condition={!calendarEvent}>Loading...</If>
					<Else>
						{!!calendarEvent && (
							<Column justify="space-between" style={{ height: "100%" }}>
								<CalendarEventForm.ControlledDetail
									calendarEvent={calendarEvent}
									formStateArgs={formStateArgs}
									onSuccess={fullOnSuccess}
									{...props}
								/>

								<Row justify="center" style={{ marginTop: "0.75rem" }}>
									<CalendarEventDeletePermissionRequired isAuthor={calendarEvent.author?.isCurrent ?? false}>
										<CalendarEventDeleteButton instanceID={calendarEvent.id} size="small" />
									</CalendarEventDeletePermissionRequired>
								</Row>
							</Column>
						)}
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}
